import { twClassNames } from "@scripts/react/helpers/twClassNames";
import React from "react";
import { IconFrame } from "@scripts/react/components/IconFrame";

const IconFrameComponent = IconFrame as any;

interface ActionCardProps {
    title: string;
    description?: React.ReactNode;
    icon?: string;
    children?: React.ReactNode;
    type?: "primary" | "secondary";
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
    expandedContent?: React.ReactNode;
    hotkeys?: string[];
}

const STYLE_CONFIG = {
    primary: {
        border: { default: "border-brand", expanded: "border-brand" },
        interactive: "hover:border-brand-hover hover:bg-tint cursor-pointer",
        title: "text-brand",
        expandedContentBorder: "border-gray-300"
    },
    secondary: {
        border: { default: "border-gray-300", expanded: "border-gray-300" },
        interactive: "hover:border-gray-400 hover:bg-gray-50 cursor-pointer",
        title: "text-secondary",
        icon: "text-base text-secondary group-hover:text-primary",
        expandedContentBorder: "border-gray-300"
    }
} as const;

export const ActionCard = ({
    title,
    description,
    icon,
    onClick,
    children,
    type = "secondary",
    disabled,
    className,
    expandedContent,
    hotkeys
}: ActionCardProps) => {
    const styles = STYLE_CONFIG[type];
    const isPrimary = type === "primary";
    const isExpanded = !!expandedContent;

    const getContainerClasses = () => {
        const borderClass = isExpanded ? styles.border.expanded : styles.border.default;
        const interactiveClass = disabled || isExpanded ? undefined : styles.interactive;
        const disabledClass = disabled ? "opacity-50 cursor-not-allowed" : undefined;
        const groupClass = disabled ? undefined : "group";

        return twClassNames(
            "flex flex-col rounded-lg border transition-all duration-300 overflow-hidden",
            groupClass,
            borderClass,
            interactiveClass,
            disabledClass,
            className
        );
    };

    const getTitleClasses = () => {
        return twClassNames("text-base font-medium", styles.title);
    };

    const getDescriptionClasses = () => {
        return "text-sm font-normal text-tertiary group-hover:text-brand";
    };

    const getIconClass = (iconString: string) => {
        const prefix = isPrimary ? "far" : "fal";
        return iconString.replace(/^far|^fal/, prefix);
    };

    const renderIcon = () => {
        if (!icon) return null;

        if (isPrimary) {
            return <IconFrameComponent icon={icon} size="md" color="purple" />;
        }

        const secondaryStyles = styles as typeof STYLE_CONFIG.secondary;
        return <i className={twClassNames(getIconClass(icon), secondaryStyles.icon)} />;
    };

    return (
        <div
            className={getContainerClasses()}
            onClick={disabled || isExpanded ? undefined : onClick}
        >
            <div
                className={twClassNames(
                    "flex flex-row",
                    isPrimary ? "items-start gap-3 p-3" : "items-center gap-4 px-5 py-3"
                )}
            >
                {renderIcon()}
                <div className="flex flex-1 flex-col gap-0">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className={getTitleClasses()}>{title}</h3>
                        {hotkeys && hotkeys.length > 0 && (
                            <div
                                className={twClassNames(
                                    "hotkeysContainer",
                                    disabled && "opacity-30"
                                )}
                            >
                                {hotkeys.map((key, index) => (
                                    <span
                                        key={index}
                                        className={twClassNames("hotkey", isPrimary && "brand")}
                                    >
                                        {key}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {description && <p className={getDescriptionClasses()}>{description}</p>}
                    {children}
                </div>
            </div>
            {expandedContent && (
                <div className={twClassNames("border-t bg-gray-50", styles.expandedContentBorder)}>
                    {expandedContent}
                </div>
            )}
        </div>
    );
};
